import React, { useCallback, useMemo, useRef, useState } from "react";
import type { RouteComponentProps } from "react-router";
import { withRouter } from "react-router-dom";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { debounce } from "lodash";

import { ProseReact } from "~/lib/prose";
import JumpTo from "~/search/JumpTo";
import SearchForm from "~/search/SearchForm";
import { IconButton, Modal } from "~/ui/legacyFellowIndex";

import styles from "./css/SearchModal.module.css";

type SearchModalProps = RouteComponentProps & {
	initialMode?: "quick" | "global";
	initialQuery?: string;
	searchSession?: string;
	open: boolean;
	onClose: () => void;
};

const SearchModal: React.FunctionComponent<SearchModalProps> = props => {
	const [searchQuery, setSearchQuery] = useState(props.initialQuery ?? "");
	const [searchInput, setSearchInput] = useState(searchQuery); // current search query value, controlled here

	const { onClose } = props;

	const handleQueryChange = useCallback((query: string) => {
		setSearchQuery(query);
		setSearchInput(query);
	}, []);

	const debouncedSyncSearch = useMemo(() => debounce(handleQueryChange, 250), [handleQueryChange]);

	const onSearchQueryChange = useCallback(
		(value: string) => {
			if (value === "") {
				setSearchQuery(value);
			}

			setSearchInput(value);
			debouncedSyncSearch(value);
		},
		[debouncedSyncSearch],
	);

	const onSearchQueryClear = useCallback(() => {
		setSearchQuery("");
		setSearchInput("");
	}, []);

	const handleClose = useCallback(() => {
		setSearchQuery("");
		setSearchInput("");
		onClose();
	}, [onClose]);

	const placeholder = "Search for people, meetings, personal notes and keywords";

	const searchFormRef = useRef<ProseReact>(null);

	return (
		<Modal isOpen={props.open} onClose={handleClose} className={styles.searchModal} initialFocusRef={searchFormRef}>
			<Modal.Header className={styles.modalHeader}>
				<SearchForm
					value={searchInput}
					onChange={onSearchQueryChange}
					onClear={onSearchQueryClear}
					placeholder={placeholder}
					ref={searchFormRef}
					className={styles.searchForm}
				/>
				<IconButton title="Close" icon={faTimes} className={styles.closeButtonOffset} onClick={handleClose} />
			</Modal.Header>

			<JumpTo query={searchQuery} onClose={handleClose} />
		</Modal>
	);
};

export default React.memo(withRouter(SearchModal));
